/**
 * Company: SPHMedia
 * Description: [Fetches recommended articles for myBH]
 */

import fetchBHApi from "@helper/fetchBHApi";
import { useCallback, useEffect, useState } from "react";

import { ProcessedArticleData } from "./transformer/useOSResponse";

type useRecommendedArticlesData = {
  articles: ProcessedArticleData[];
  loading: boolean;
  error: boolean;
  pagination: {
    current: number;
    next: number;
    totalPages: number;
  } | null;
  refetch: () => Promise<void>;
  loadMore: () => Promise<ProcessedArticleData[]>;
  hasMore: boolean;
};

type useRecommendedArticlesProps = {
  tags?: string[];
  size?: number;
  enabled?: boolean;
  initialArticles?: ProcessedArticleData[]; // Optional initial articles to populate the state
};

type RecommendedPagination = NonNullable<
  useRecommendedArticlesData["pagination"]
>;

const EMPTY_PAGINATION: RecommendedPagination = {
  current: 0,
  next: 0,
  totalPages: 0,
};

type RecommendedArticlesResponse =
  | ProcessedArticleData[]
  | {
      data?: ProcessedArticleData[];
      articles?: ProcessedArticleData[];
      pagination?: RecommendedPagination;
    };

/**
 * Custom React Hook: useRecommendedArticlesHook
 * Description: [Fetches recommended articles based on user id or device id for myBH recommended page]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useRecommendedArticles({
  tags,
  size,
  enabled = true,
  initialArticles = [],
}: useRecommendedArticlesProps): useRecommendedArticlesData {
  const [articles, setArticles] =
    useState<ProcessedArticleData[]>(initialArticles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [pagination, setPagination] =
    useState<useRecommendedArticlesData["pagination"]>(EMPTY_PAGINATION);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = size ?? 10;

  const getArticlesFromResponse = useCallback(
    (response: RecommendedArticlesResponse): ProcessedArticleData[] => {
      if (Array.isArray(response)) {
        return response;
      }

      return response.data ?? response.articles ?? [];
    },
    [],
  );

  const fetchArticles = useCallback(async () => {
    if (!tags?.[0]) return;

    setLoading(true);
    setError(false);

    try {
      const allTagAliases = tags.join(",");
      const queryParams = new URLSearchParams({
        tagAlias: allTagAliases,
        ...(size !== undefined && { size: String(size) }),
        isMyBH: "true",
      }).toString();
      const response = await fetchBHApi<RecommendedArticlesResponse>(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      const nextArticles = getArticlesFromResponse(response);
      const nextHasMore = nextArticles.length >= pageSize;
      setArticles(nextArticles);
      setHasMore(nextHasMore);
      setPagination({
        current: nextArticles.length > 0 ? 1 : 0,
        next: nextHasMore ? 2 : 0,
        totalPages: 0,
      });
    } catch (err) {
      console.error("Failed to fetch recommended articles", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [getArticlesFromResponse, pageSize, size, tags]);

  const loadMore = useCallback(async (): Promise<ProcessedArticleData[]> => {
    if (!tags?.[0] || !hasMore || loading || articles.length === 0) {
      return [];
    }

    const lastArticle = articles.at(-1);
    if (!lastArticle || !lastArticle.sort.length) {
      setHasMore(false);
      return [];
    }

    setLoading(true);
    setError(false);

    try {
      const queryParams = new URLSearchParams({
        tagAlias: tags.join(","),
        sort: lastArticle.sort.join(","),
        excludeIds: articles.map((article) => article.id).join(","),
        ...(size !== undefined && { size: String(size) }),
        isMyBH: "true",
      }).toString();

      const response = await fetchBHApi<RecommendedArticlesResponse>(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      const moreArticles = getArticlesFromResponse(response);
      const existingIds = new Set(articles.map((article) => article.id));
      const uniqueMoreArticles = moreArticles.filter(
        (article) => !existingIds.has(article.id),
      );

      if (uniqueMoreArticles.length > 0) {
        setArticles((prev) => [...prev, ...uniqueMoreArticles]);
      }

      const nextHasMore = moreArticles.length >= pageSize;
      setHasMore(nextHasMore);
      setPagination((prev) => {
        const currentPage = prev?.current ?? 0;
        const nextCurrentPage =
          uniqueMoreArticles.length > 0 ? currentPage + 1 : currentPage;

        return {
          current: nextCurrentPage,
          next: nextHasMore ? nextCurrentPage + 1 : 0,
          totalPages: 0,
        };
      });

      return uniqueMoreArticles;
    } catch (err) {
      console.error("Failed to load more recommended articles", err);
      setError(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, [
    articles,
    getArticlesFromResponse,
    hasMore,
    loading,
    pageSize,
    size,
    tags,
  ]);

  // initial + dependency change fetch
  useEffect(() => {
    if (!enabled) return;

    void fetchArticles();
  }, [enabled, fetchArticles]);

  return {
    articles,
    loading,
    error,
    pagination,
    refetch: fetchArticles,
    loadMore,
    hasMore,
  };
}
